import { matchPath } from "react-router-dom";

export type EventWorkspaceView = "monitor" | "overview";

export const LIVE_DASHBOARD_ROUTES = [
  "campaign-schedule",
  "event-protection",
  "system-overview",
  "event-overview",
  "multi-chart",
  "event-monitor",
].map((dashboardSlug) => ({ path: `/live/${dashboardSlug}`, dashboardSlug }));

export const LEGACY_LIVE_DASHBOARD_ROUTES = [
  { path: "/live/protection", dashboardSlug: "campaign-schedule" },
  { path: "/live/protection/campaigns", dashboardSlug: "campaign-schedule" },
  { path: "/live/protection/campaigns/:campaignId", dashboardSlug: "campaign-schedule" },
  { path: "/live/protection/events", dashboardSlug: "event-protection" },
  { path: "/live/protection/events/:eventId", dashboardSlug: "event-protection" },
  { path: "/live/system-health", dashboardSlug: "system-overview" },
  { path: "/live/events", dashboardSlug: "event-overview" },
  { path: "/live/events/overview", dashboardSlug: "multi-chart" },
  { path: "/live/events/:eventId/overview", dashboardSlug: "multi-chart" },
  { path: "/live/events/monitor", dashboardSlug: "event-monitor" },
  { path: "/live/events/:eventId/monitor", dashboardSlug: "event-monitor" },
] as const;

export function resolveLiveDashboardHref(destination: string): string {
  const target = new URL(destination, "https://nexus.invalid");
  for (const { path, dashboardSlug } of LEGACY_LIVE_DASHBOARD_ROUTES) {
    const match = matchPath(path, target.pathname);
    if (!match) continue;
    target.pathname = `/live/${dashboardSlug}`;
    if (match.params.eventId) target.searchParams.set("event_id", match.params.eventId);
    if (match.params.campaignId) target.searchParams.set("campaign_id", match.params.campaignId);
    return `${target.pathname}${target.search}${target.hash}`;
  }
  return destination;
}

/** Overview retains Monitor's selection as return context, not as a data filter. */
export function buildEventWorkspaceHref(
  view: EventWorkspaceView,
  eventId: string | null,
  protectedEventId: string | null = null,
  tenant?: { id: string; label?: string },
): string {
  const base = `/live/${view === "monitor" ? "event-monitor" : "multi-chart"}`;
  const query = new URLSearchParams();
  if (eventId) query.set("event_id", eventId);
  if (eventId && protectedEventId)
    query.set(
      view === "monitor" ? "protected_event_id" : "monitor_protected_event_id",
      protectedEventId,
    );
  if (tenant?.id) query.set("tenant_id", tenant.id);
  if (tenant?.label) query.set("tenant_label", tenant.label);
  const search = query.toString();
  return search ? `${base}?${search}` : base;
}

export function getMonitorProtection(view: EventWorkspaceView, search: string): string | null {
  return new URLSearchParams(search).get(
    view === "monitor" ? "protected_event_id" : "monitor_protected_event_id",
  );
}

export function buildCampaignScheduleHref(
  campaignId: string | null = null,
  status: string | null = null,
): string {
  const base = "/live/campaign-schedule";
  const query = new URLSearchParams();
  if (campaignId) query.set("campaign_id", campaignId);
  if (status && status !== "active") query.set("status", status);
  const search = query.toString();
  return search ? `${base}?${search}` : base;
}

interface EventProtectionHrefContext {
  campaignId?: string | null;
  status?: string | null;
}

export function buildEventProtectionHref(
  eventId: string | null = null,
  context: EventProtectionHrefContext = {},
): string {
  const base = "/live/event-protection";
  const query = new URLSearchParams();
  if (eventId) query.set("event_id", eventId);
  if (context.campaignId) query.set("campaign", context.campaignId);
  if (context.status && context.status !== "active") query.set("status", context.status);
  const search = query.toString();
  return search ? `${base}?${search}` : base;
}

export function buildLegacyCampaignScheduleHref(search: string): string {
  const query = new URLSearchParams(search);
  return buildCampaignScheduleHref(query.get("campaign"), query.get("status"));
}

export function buildLegacyEventProtectionHref(
  search: string,
  resolvedEventId: string | null,
): string {
  const query = new URLSearchParams(search);
  return buildEventProtectionHref(resolvedEventId ?? query.get("event_id"), {
    campaignId: query.get("campaign"),
    status: query.get("status"),
  });
}

interface LiveEventsHrefInput {
  eventId?: string | null;
  protectedEventId?: string | null;
  tenantId?: string | null;
  tenantLabel?: string | null;
}

export function buildLiveEventsHref({
  eventId,
  protectedEventId,
  tenantId,
  tenantLabel,
}: LiveEventsHrefInput = {}): string {
  const query = new URLSearchParams();
  if (eventId) query.set("event_id", eventId);
  if (protectedEventId) query.set("protected_event_id", protectedEventId);
  if (tenantId) query.set("tenant_id", tenantId);
  if (tenantLabel) query.set("tenant_label", tenantLabel);
  const search = query.toString();
  return search ? `/live/event-overview?${search}` : "/live/event-overview";
}
